import {ChangeEvent, useRef, useState} from 'react';
import {defaultValues} from './content';
import {
  StyledFileUploader,
  StyledFileUploaderButton,
  StyledFileUploaderContent,
  StyledFileUploaderDefaultMsg,
  StyledFileUploaderErrorList,
  StyledFileUploaderLabel,
  StyledFileUploaderList,
  StyledFileUploaderTopRow,
  StyledRemoveButton,
} from './styles';
import {FileUploaderProps} from './typings';

const getFileExtension = (fileName: string) => {
  const extensionIndex = fileName.lastIndexOf('.');

  return extensionIndex >= 0 ? fileName.slice(extensionIndex).toLowerCase() : '';
};

const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

const formatFileSize = (bytes: number) => {
  if (!bytes) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** unitIndex;
  const precision = unitIndex === 0 || size >= 10 ? 0 : 1;

  return `${size.toFixed(precision)} ${units[unitIndex]}`;
};

const FileUploader = ({
  label = defaultValues.label,
  id,
  name,
  multiple = true,
  noFilesMsg = defaultValues.noFilesMsg,
  buttonText = defaultValues.buttonText,
  allowedExtensions = defaultValues.extensions,
  allowedMimeTypes = defaultValues.mimeTypes,
  maxFileSize = defaultValues.maxFileSize,
  maxTotalSize,
  onFilesChange,
}: FileUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const normalizedAllowedExtensions = allowedExtensions.map((extension) => extension.toLowerCase());
  const normalizedAllowedMimeTypes = allowedMimeTypes.map((mimeType) => mimeType.toLowerCase());
  const acceptedFileTypes = [...normalizedAllowedExtensions, ...normalizedAllowedMimeTypes].join(
    ',',
  );

  const updateFiles = (nextFiles: File[]) => {
    setFiles(nextFiles);
    onFilesChange?.(nextFiles);
  };

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    const nextErrors: string[] = [];
    const nextFiles = multiple ? [...files] : [];
    const existingFileKeys = new Set(nextFiles.map(getFileKey));
    let nextTotalSize = nextFiles.reduce((total, file) => total + file.size, 0);

    selectedFiles.forEach((file) => {
      const fileExtension = getFileExtension(file.name);
      const fileMimeType = file.type.toLowerCase();
      const fileKey = getFileKey(file);

      if (existingFileKeys.has(fileKey)) {
        nextErrors.push(`${file.name} has already been selected.`);
        return;
      }

      if (
        normalizedAllowedExtensions.length &&
        !normalizedAllowedExtensions.includes(fileExtension)
      ) {
        nextErrors.push(`${file.name} is not an allowed file type.`);
        return;
      }

      if (normalizedAllowedMimeTypes.length && !normalizedAllowedMimeTypes.includes(fileMimeType)) {
        nextErrors.push(`${file.name} is not an allowed file type.`);
        return;
      }

      if (maxFileSize && file.size > maxFileSize) {
        nextErrors.push(`${file.name} is larger than ${formatFileSize(maxFileSize)}.`);
        return;
      }

      if (maxTotalSize && nextTotalSize + file.size > maxTotalSize) {
        nextErrors.push(`Selected files cannot exceed ${formatFileSize(maxTotalSize)} in total.`);
        return;
      }

      nextFiles.push(file);
      existingFileKeys.add(fileKey);
      nextTotalSize += file.size;
    });

    setErrors(nextErrors);
    updateFiles(nextFiles);
    event.target.value = '';
  };

  const onFileRemove = (fileKey: string) => {
    const nextFiles = files.filter((file) => getFileKey(file) !== fileKey);

    setErrors([]);
    updateFiles(nextFiles);
  };

  return (
    <StyledFileUploader>
      <StyledFileUploaderTopRow>
        <StyledFileUploaderLabel htmlFor={id}>
          {label}
          {!!files.length && ` (${files.length} file${files.length > 1 ? 's' : ''})`}
        </StyledFileUploaderLabel>

        <StyledFileUploaderButton
          type="button"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          {buttonText}
        </StyledFileUploaderButton>
      </StyledFileUploaderTopRow>

      <input
        ref={inputRef}
        type="file"
        id={id}
        name={name}
        multiple={multiple}
        accept={acceptedFileTypes}
        onChange={onFileSelect}
        hidden
      />

      <StyledFileUploaderContent>
        {!!errors.length && (
          <StyledFileUploaderErrorList aria-live="polite">
            {errors.map((error, index) => (
              <li key={`${error}-${index}`}>{error}</li>
            ))}
          </StyledFileUploaderErrorList>
        )}

        {!!files.length ? (
          <StyledFileUploaderList>
            {files.map((file) => (
              <li key={getFileKey(file)}>
                <span>{file.name}</span>

                <span>{formatFileSize(file.size)}</span>

                <StyledRemoveButton
                  icon="close"
                  title={`Remove ${file.name}`}
                  onClick={() => onFileRemove(getFileKey(file))}
                />
              </li>
            ))}
          </StyledFileUploaderList>
        ) : (
          <StyledFileUploaderDefaultMsg>{noFilesMsg}</StyledFileUploaderDefaultMsg>
        )}
      </StyledFileUploaderContent>
    </StyledFileUploader>
  );
};

export default FileUploader;
